import React from "react";
import { Link } from "react-router-dom";
import { Train, ScanLine, Bot, BrainCircuit } from "lucide-react";
import { SectionLabel, Reveal, BtnAccent, BtnGhost, ArrowLink, Stat } from "../components/common";
import { IMG, HERO_STATS, TICKER, DOMAINS, MACHINES, FEATURED, PHILOSOPHY_TAGS, AI_STEPS } from "../mock";

const ICONS = { Train, ScanLine, Bot, BrainCircuit };

function Ticker() {
  const items = [...TICKER, ...TICKER];
  return (
    <div className="marquee-mask relative overflow-hidden border-y border-white/10 bg-[#0d0d0d] py-4">
      <div className="marquee-track">
        {items.map((t, i) => (
          <span key={i} className="mx-6 inline-flex items-center gap-6 eyebrow text-neutral-400">
            {t} <span className="h-1 w-1 rounded-full bg-[#c3e63c]" />
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const featured = FEATURED.map((id) => MACHINES.find((m) => m.id === id));
  return (
    <main>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0a0a0a]">
        <img src={IMG.heroHome} alt="Railway machinery" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
        <div className="dot-grid absolute inset-0 opacity-25" />
        <div className="relative mx-auto w-full max-w-[1400px] px-5 md:px-10 pt-28 pb-20">
          <SectionLabel>A Garry Industries Ventures · Intelligent Railway Engineering · India</SectionLabel>
          <h1 className="font-display mt-7 max-w-5xl text-5xl md:text-7xl lg:text-[5.7rem] font-bold leading-[0.98] tracking-tight">
            Engineering the <span className="text-[#c3e63c]">future</span> of railway infrastructure.
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-neutral-300">
            Railway Machines is building an integrated platform of infrastructure machinery, intelligent maintenance equipment, robotics and AI-enabled railway technologies — designed for India, engineered to global standards.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <BtnAccent to="/machines">Explore the Ecosystem</BtnAccent>
            <BtnGhost to="/about">Our Vision</BtnGhost>
          </div>
        </div>
      </section>

      {/* HERO STATS */}
      <section className="bg-[#0a0a0a] border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10 border-x border-white/10">
            {HERO_STATS.map((s, i) => (
              <div key={i} className="p-7 md:p-9">
                <div className="font-display text-3xl md:text-4xl font-bold text-white">{s.value}</div>
                <div className="eyebrow mt-3 text-neutral-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Ticker />

      {/* VISION */}
      <section className="relative bg-[#f2f1ec] text-[#0a0a0a] py-24 md:py-32">
        <div className="dot-grid-dark absolute inset-0 opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal><SectionLabel light>A Garry Industries Ventures · The Vision</SectionLabel></Reveal>
          <div className="mt-8 grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
            <Reveal>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight">
                Not a single product. An entire ecosystem.
              </h2>
            </Reveal>
            <Reveal delay={120} className="flex flex-col justify-center">
              <p className="text-[#4a4a44] leading-relaxed">
                Railway networks are among the most demanding engineering environments in the world. Maintaining them at scale requires machinery that is precise, durable and increasingly intelligent.
              </p>
              <p className="mt-5 text-[#4a4a44] leading-relaxed">
                We are engineering a complete family of railway infrastructure machines and digital systems — designed to work together, share data and evolve toward autonomy.
              </p>
              <div className="mt-7"><ArrowLink to="/about" light>Read our long-term vision</ArrowLink></div>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-px bg-[#d8d7d0] sm:grid-cols-2 lg:grid-cols-4">
            {DOMAINS.map((d, i) => {
              const Icon = ICONS[d.icon];
              return (
                <Reveal key={i} delay={i * 80} className="group bg-[#f2f1ec] p-8 hover:bg-white transition-colors">
                  <Icon size={26} strokeWidth={1.5} className="text-[#0a0a0a] group-hover:text-[#8ba817] transition-colors" />
                  <h3 className="font-display mt-6 text-xl font-semibold">{d.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4a4a44]">{d.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MACHINE ECOSYSTEM */}
      <section className="relative bg-[#0a0a0a] py-24 md:py-32">
        <div className="dot-grid absolute inset-0 opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal><SectionLabel>A Garry Industries Ventures · Machine Ecosystem</SectionLabel></Reveal>
          <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight max-w-2xl">
                Concept machines, engineered for reality.
              </h2>
            </Reveal>
            <Reveal delay={100}><ArrowLink to="/machines">View all machines</ArrowLink></Reveal>
          </div>

          <div className="mt-14 grid gap-4 lg:grid-cols-2">
            <Reveal className="group relative overflow-hidden border border-white/10 lg:row-span-2">
              <Link to="/machines" className="block h-full">
                <div className="relative h-72 lg:h-full min-h-[420px] overflow-hidden">
                  <img src={featured[0].image} alt={featured[0].name} className="img-zoom h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/20 to-transparent" />
                </div>
                <div className="absolute bottom-0 p-8">
                  <div className="eyebrow text-[#c3e63c]">{featured[0].category}</div>
                  <h3 className="font-display mt-2 text-2xl md:text-3xl font-bold">{featured[0].name}</h3>
                  <p className="mt-3 max-w-md text-sm text-neutral-300">{featured[0].desc}</p>
                </div>
              </Link>
            </Reveal>

            <div className="grid gap-4 sm:grid-cols-2">
              {featured.slice(1).map((m, i) => (
                <Reveal key={m.id} delay={i * 70} className="group relative overflow-hidden border border-white/10">
                  <Link to="/machines" className="block">
                    <div className="relative h-52 overflow-hidden">
                      <img src={m.image} alt={m.name} className="img-zoom h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                    </div>
                    <div className="absolute bottom-0 p-5">
                      <div className="eyebrow text-[#c3e63c]">{m.category}</div>
                      <h3 className="font-display mt-1.5 text-lg font-semibold">{m.name}</h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ENGINEERING PHILOSOPHY */}
      <section className="bg-[#f2f1ec] text-[#0a0a0a] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 lg:items-center">
            <Reveal className="relative overflow-hidden border border-[#0a0a0a]/10 group">
              <img src={IMG.gears} alt="Engineering detail" className="img-zoom h-[360px] md:h-[460px] w-full object-cover" />
            </Reveal>
            <div>
              <Reveal><SectionLabel light>A Garry Industries Ventures · Engineering Philosophy</SectionLabel></Reveal>
              <Reveal><h2 className="font-display mt-6 text-4xl md:text-5xl font-bold leading-[1.03] tracking-tight">Every component earns its place.</h2></Reveal>
              <Reveal delay={100}><p className="mt-6 text-[#4a4a44] leading-relaxed max-w-lg">Nothing decorative. Correct wheelsets, logical bogie arrangements, real maintenance access, hydraulic systems, sensor placement and safety by design. We engineer machines the way they are built and serviced.</p></Reveal>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {PHILOSOPHY_TAGS.map((t, i) => (
                  <Reveal key={i} delay={i * 60} className="flex items-center gap-3 border-l-2 border-[#c3e63c] pl-3 py-1">
                    <span className="text-sm font-medium">{t}</span>
                  </Reveal>
                ))}
              </div>
              <div className="mt-9"><ArrowLink to="/engineering" light>Engineering & technology</ArrowLink></div>
            </div>
          </div>
        </div>
      </section>

      {/* AI PLATFORM */}
      <section className="relative overflow-hidden bg-[#0a0a0a] py-24 md:py-32">
        <img src={IMG.dataViz} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/85 to-[#0a0a0a]" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
            <div>
              <Reveal><SectionLabel>A Garry Industries Ventures · Digital & AI Platform</SectionLabel></Reveal>
              <Reveal><h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight">A living digital twin of the network.</h2></Reveal>
              <Reveal delay={100}><p className="mt-6 max-w-lg text-neutral-300 leading-relaxed">Fleet telemetry, machine health and inspection data flow into a unified platform — powering predictive maintenance, live diagnostics and autonomous decision support.</p></Reveal>
              <div className="mt-9"><BtnAccent to="/ai-platform">Explore the AI Platform</BtnAccent></div>
            </div>
            <div className="flex flex-col justify-center gap-px bg-white/10">
              {AI_STEPS.map((s, i) => (
                <Reveal key={i} delay={i * 90} className="flex items-start gap-6 bg-[#0a0a0a] p-6">
                  <span className="font-mono2 text-[#c3e63c] text-sm pt-1">{s.n}</span>
                  <div>
                    <h3 className="font-display text-xl font-semibold">{s.title}</h3>
                    <p className="mt-1.5 text-sm text-neutral-400">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDIA */}
      <section className="bg-[#f2f1ec] text-[#0a0a0a] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal><SectionLabel light>A Garry Industries Ventures · Designed in India</SectionLabel></Reveal>
          <div className="mt-8 grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
            <Reveal><h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight">Strengthening indigenous engineering capability.</h2></Reveal>
            <Reveal delay={100} className="flex items-center">
              <p className="text-[#4a4a44] leading-relaxed">Engineered for demanding railway conditions and built to global engineering standards — with future export capability. As a {"Garry Industries"} venture, we are contributing to self-reliance through advanced engineering, manufacturing and digital technology.</p>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#d8d7d0]">
            {[["4","Product families"],["11+","Concept machines"],["100%","Original engineering"],["Global","Standards target"]].map(([v,l],i) => (
              <Reveal key={i} delay={i*70} className="bg-[#f2f1ec] p-8"><Stat value={v} label={l} light /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CtaBand />
    </main>
  );
}

export function CtaBand() {
  return (
    <section className="relative overflow-hidden bg-[#c3e63c] text-[#0a0a0a]">
      <div className="dot-grid-dark absolute inset-0 opacity-30 pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
        <Reveal><SectionLabel light>A Garry Industries Ventures</SectionLabel></Reveal>
        <Reveal>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight max-w-3xl">
            Let's build the future of railway engineering together.
          </h2>
        </Reveal>
        <div className="mt-9 flex flex-wrap gap-4">
          <Link to="/investors" className="btn-accent inline-flex items-center gap-2.5 bg-[#0a0a0a] px-6 py-3.5 eyebrow text-white hover:bg-black">For Investors</Link>
          <Link to="/contact" className="btn-accent inline-flex items-center gap-2.5 border border-[#0a0a0a]/40 px-6 py-3.5 eyebrow text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white">Get in Touch</Link>
        </div>
      </div>
    </section>
  );
}
